import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, dateFmt, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";
import { ArrowUpRight, Wallet, AlertCircle, Receipt, ShieldAlert, Calendar, CheckCircle2, PiggyBank } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [reserve, setReserve] = useState(null);
  const [spendAnalytics, setSpendAnalytics] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);

  const fetchReserve = async () => {
    try {
      const r = await api.get("/tax/reserve");
      setReserve(r.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, inv, comp, res, spend] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/invoices"),
          api.get("/tax/compliance"),
          api.get("/tax/reserve"),
          api.get("/expenses/analytics"),
        ]);
        setData(s.data);
        setInvoices((inv.data || []).slice(0, 5));
        setCompliance(comp.data);
        setReserve(res.data);
        setSpendAnalytics(spend.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!data) {
    return <div className="text-sm text-[#5a5a53]" data-testid="dashboard-loading">Loading…</div>;
  }

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="tc-overline mb-2">Financial overview</p>
          <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Your studio&apos;s finances</h1>
        </div>
        <Link to="/app/invoices">
          <Button data-testid="dashboard-new-invoice" className="bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md">
            New invoice <ArrowUpRight size={16} className="ml-1" />
          </Button>
        </Link>
      </div>

      {/* Top KPI strip — health of the business (profit-led, not income-led) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
        <Kpi
          label="Net Profit (YTD)"
          value={inr(data.net_profit)}
          icon={Wallet}
          accent={data.net_profit >= 0 ? "#4a6b53" : "#b83a26"}
          testId="kpi-net-profit"
          sub={`Cash in − Cash out`}
        />
        <Kpi
          label="Cash In (YTD)"
          value={inr(data.total_collected)}
          icon={ArrowUpRight}
          accent="#0a0a0a"
          testId="kpi-cash-in"
          sub={`Invoices ${inr(data.invoice_collected)} · Other ${inr(data.other_income || 0)}`}
        />
        <Kpi
          label="Cash Out (YTD)"
          value={inr(data.total_expenses)}
          icon={Receipt}
          accent="#d95d39"
          testId="kpi-cash-out"
          sub="All expenses, net of GST"
        />
        <TaxReserveKpi reserve={reserve} onClick={() => setReserveOpen(true)} />
      </div>

      {/* Secondary KPI strip — what's pending + tax position */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#e5e5e0] border border-[#e5e5e0] rounded-md overflow-hidden">
        <Kpi label="Pending Collections" value={inr(data.total_pending)} icon={AlertCircle} accent="#d4b872" testId="kpi-pending" sub="Invoices issued, not paid" />
        <Kpi label="GST Pending" value={inr(data.gst_pending ?? data.net_gst_payable)} icon={Receipt} accent="#d95d39" testId="kpi-gst-pending"
          sub={data.net_gst_payable > 0
            ? `${inr(data.gst_paid_total || 0)} paid of ${inr(data.net_gst_payable)}`
            : (data.gst_paid_total > 0 ? `${inr(data.gst_paid_total)} paid (no liability)` : "No liability yet")} />
        <Kpi label="Output GST (YTD)" value={inr(data.output_gst)} icon={Receipt} accent="#0a0a0a" testId="kpi-output-gst" sub={`− ${inr(data.input_gst)} input credit`} />
        <Kpi label="Total Billed (YTD)" value={inr(data.total_billed)} icon={Wallet} accent="#0a0a0a" testId="kpi-billed" sub={`${data.invoices_count} invoices issued`} />
      </div>

      {/* GST alert banner (only when approaching/exceeded and not registered) */}
      {compliance?.gst && !compliance.gst.registered && (compliance.gst.status === "approaching" || compliance.gst.status === "exceeded") && (
        <div
          data-testid="gst-alert-banner"
          className={`tc-card p-5 flex items-start gap-4 ${
            compliance.gst.status === "exceeded"
              ? "bg-[#fff4f1] border-[#f5c6b9]"
              : "bg-[#fffaf0] border-[#f2d9a8]"
          }`}
        >
          <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
            compliance.gst.status === "exceeded" ? "bg-[#b83a26] text-white" : "bg-[#d4b872] text-[#0a0a0a]"
          }`}>
            <ShieldAlert size={18} />
          </div>
          <div className="flex-1">
            <p className="tc-overline" style={{ color: compliance.gst.status === "exceeded" ? "#b83a26" : "#8c6d2a" }}>
              {compliance.gst.status === "exceeded" ? "GST registration overdue" : "Heads up · GST threshold approaching"}
            </p>
            <h3 className="font-['Outfit'] text-lg font-medium tracking-tight mt-1 text-[#0a0a0a]">
              {compliance.gst.message}
            </h3>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              <div className="flex items-baseline gap-1.5 text-xs text-[#5a5a53]">
                FY turnover: <span className="font-mono font-bold text-[#0a0a0a]">{inr(compliance.gst.turnover_ytd)}</span>
                <span className="text-[#a3a39b]">/ {inr(compliance.gst.threshold)}</span>
                <span className="text-[#d95d39] font-bold">· {compliance.gst.percent}%</span>
              </div>
              <Link to="/app/settings" className="text-xs text-[#d95d39] hover:underline font-bold tracking-wider uppercase">
                Update GSTIN →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Overdue invoices banner */}
      {data.overdue_count > 0 && (
        <div
          data-testid="overdue-alert-banner"
          className="tc-card p-5 flex items-start gap-4 bg-[#fff4f1] border-[#f5c6b9]"
        >
          <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-[#b83a26] text-white">
            <AlertCircle size={18} />
          </div>
          <div className="flex-1">
            <p className="tc-overline" style={{ color: "#b83a26" }}>Overdue invoices</p>
            <h3 className="font-['Outfit'] text-lg font-medium tracking-tight mt-1 text-[#0a0a0a]">
              {data.overdue_count} invoice{data.overdue_count > 1 ? "s" : ""} past due · {inr(data.total_overdue)} uncollected
              {data.overdue_oldest_days > 0 && ` · oldest is ${data.overdue_oldest_days} day${data.overdue_oldest_days > 1 ? "s" : ""} late`}
            </h3>
            <div className="mt-3 flex items-center gap-4 flex-wrap">
              <Link to="/app/invoices" className="text-xs text-[#b83a26] hover:underline font-bold tracking-wider uppercase">
                Review overdue →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Compliance pulse */}
      {compliance && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="compliance-pulse">
          {/* GST threshold card */}
          <GstCard gst={compliance.gst} fy={compliance.fy_label} />
          {/* Advance tax card */}
          <AdvanceTaxCard advance={compliance.advance_tax} fy={compliance.fy_label} />
        </div>
      )}

      {/* Chart + side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 tc-card p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="tc-overline">Cashflow</p>
              <h2 className="font-['Outfit'] text-2xl font-medium tracking-tight mt-1">Last 6 months</h2>
            </div>
            <div className="flex items-center gap-3 text-xs text-[#5a5a53]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#d95d39] inline-block" />Income</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#4a6b53] inline-block" />Expenses</span>
            </div>
          </div>
          <div className="h-72 mt-4" data-testid="cashflow-chart">
            <ResponsiveContainer>
              <AreaChart data={data.chart}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d95d39" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#d95d39" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4a6b53" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#4a6b53" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e5e5e0" vertical={false} />
                <XAxis dataKey="month" stroke="#a3a39b" tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke="#a3a39b" tickLine={false} axisLine={false} fontSize={11}
                  tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "none", borderRadius: 6, color: "#fafaf8", fontSize: 12 }}
                  formatter={(v) => inr(v)}
                />
                <Area type="monotone" dataKey="income" stroke="#d95d39" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="expenses" stroke="#4a6b53" strokeWidth={2} fill="url(#g2)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-6">
          <div className="tc-card p-6">
            <p className="tc-overline">At a glance</p>
            <div className="mt-4 space-y-3 text-sm">
              <Row label="Clients" value={String(data.clients_count)} />
              <Row label="Invoices issued" value={String(data.invoices_count)} />
              <Row label="Other income entries" value={String(data.incomes_count || 0)} />
              <Row label="Total billed" value={inr(data.total_billed)} />
              <Row label="Other income" value={inr(data.other_income || 0)} />
              <Row label="Total expenses" value={inr(data.total_expenses)} />
              <Row label="Output GST" value={inr(data.output_gst)} />
              <Row label="Input credit" value={inr(data.input_gst)} />
              <Row label="Net business income (pre-tax)" value={inr(data.net_profit)} />
            </div>
          </div>
          <SpendMiniCard analytics={spendAnalytics} />
          <div className="tc-card p-6 bg-[#0a0a0a] text-[#fafaf8]">
            <p className="tc-overline" style={{ color: "#d95d39" }}>AI co-pilot</p>
            <p className="mt-3 text-sm leading-relaxed text-[#a3a39b]">
              Wondering whether the new tax regime is right for you this year?
              Ask the co-pilot — it speaks Indian tax fluently.
            </p>
            <Link to="/app/ai">
              <Button data-testid="dashboard-ai-cta" className="mt-5 bg-[#d95d39] hover:bg-[#c84c35] text-white rounded-md w-full">
                Ask AI
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Recent invoices */}
      <div className="tc-card overflow-hidden overflow-x-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#e5e5e0]">
          <div>
            <p className="tc-overline">Latest</p>
            <h2 className="font-['Outfit'] text-2xl font-medium tracking-tight mt-1">Recent invoices</h2>
          </div>
          <Link to="/app/invoices" className="text-sm text-[#5a5a53] hover:text-[#0a0a0a]">View all →</Link>
        </div>
        {invoices.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#5a5a53]">
            No invoices yet. <Link to="/app/invoices" className="text-[#d95d39] hover:underline">Create your first one.</Link>
          </div>
        ) : (
          <table className="w-full text-sm min-w-[560px]" data-testid="dashboard-invoice-table">
            <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
              <tr>
                <th className="text-left px-6 py-3 font-bold">Invoice #</th>
                <th className="text-left px-6 py-3 font-bold">Client</th>
                <th className="text-left px-6 py-3 font-bold">Issue date</th>
                <th className="text-right px-6 py-3 font-bold">Amount</th>
                <th className="text-left px-6 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.id} className="border-t border-[#e5e5e0] hover:bg-[#fafaf8]">
                  <td className="px-6 py-3 font-mono text-xs">{i.invoice_number}</td>
                  <td className="px-6 py-3">{i.client_snapshot?.name || "—"}</td>
                  <td className="px-6 py-3 text-[#5a5a53]">{dateFmt(i.issue_date)}</td>
                  <td className="px-6 py-3 text-right font-medium">{inr(i.total)}</td>
                  <td className="px-6 py-3"><StatusBadge status={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TaxReserveDrawer open={reserveOpen} onOpenChange={setReserveOpen} reserve={reserve} onParked={fetchReserve} />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testId, sub }) {
  return (
    <div className="bg-white p-4 md:p-6 relative min-w-0" data-testid={testId}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#5a5a53] leading-tight">{label}</p>
        <Icon size={14} style={{ color: accent }} className="shrink-0" />
      </div>
      <div
        className="font-['Outfit'] text-xl md:text-3xl font-bold tracking-tight break-words"
        style={{ color: accent }}
      >
        {value}
      </div>
      {sub && <div className="mt-2 text-[11px] text-[#a3a39b] font-mono truncate">{sub}</div>}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-[#e5e5e0] last:border-b-0 pb-2 last:pb-0">
      <span className="text-[#5a5a53]">{label}</span>
      <span className="font-medium font-mono">{value}</span>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: "bg-[#f4f1eb] text-[#5a5a53]",
    sent: "bg-[#fff4ed] text-[#d95d39]",
    viewed: "bg-[#eef1f7] text-[#3d5a8a]",
    partially_paid: "bg-[#fff7e0] text-[#8c6d2a]",
    paid: "bg-[#eef3ee] text-[#4a6b53]",
    overdue: "bg-[#ffece8] text-[#b83a26]",
    disputed: "bg-[#f3eafd] text-[#6b4aa0]",
    cancelled: "bg-[#e9e9e6] text-[#5a5a53] line-through",
  };
  const label = (status || "draft").replace("_", " ");
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${map[status] || map.draft}`}>
      {label}
    </span>
  );
}

function GstCard({ gst, fy }) {
  // 3 distinct views:
  //   A) Registered users → current-month liability + next return due dates
  //   B) Unregistered, crossed threshold → big urgency banner
  //   C) Unregistered, under threshold → original progress bar
  if (gst.registered) {
    return <GstRegisteredCard gst={gst} />;
  }
  if (gst.status === "exceeded") {
    return <GstExceededCard gst={gst} fy={fy} />;
  }
  return <GstThresholdCard gst={gst} fy={fy} />;
}

function GstRegisteredCard({ gst }) {
  const due3b = new Date(gst.gstr3b_due);
  const daysToDue = Math.ceil((due3b - new Date()) / (1000 * 60 * 60 * 24));
  const dueSoon = daysToDue <= 7;
  return (
    <div className="tc-card p-6" data-testid="compliance-gst-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="tc-overline">GST liability · {gst.period_label}</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-[#4a6b53] bg-[#eef3ee] px-2 py-0.5 rounded-sm">
          <CheckCircle2 size={11} /> Registered
        </span>
      </div>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap">
        <span className="font-['Outfit'] text-2xl md:text-3xl font-bold tracking-tight font-mono break-words text-[#d95d39]">
          {inr(gst.period_net_payable)}
        </span>
        <span className="text-xs text-[#5a5a53]">net payable this period</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div className="bg-[#fafaf8] rounded-md p-3 border border-[#e5e5e0]">
          <p className="tc-overline mb-1">Output GST</p>
          <p className="font-mono font-bold text-[#0a0a0a]">{inr(gst.period_output_gst)}</p>
        </div>
        <div className="bg-[#fafaf8] rounded-md p-3 border border-[#e5e5e0]">
          <p className="tc-overline mb-1">Input credit</p>
          <p className="font-mono font-bold text-[#4a6b53]">−{inr(gst.period_input_gst)}</p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#e5e5e0] space-y-1.5 text-xs">
        <div className="flex justify-between">
          <span className="text-[#5a5a53]">GSTR-1 due</span>
          <span className="font-mono font-bold text-[#0a0a0a]">{dateFmt(gst.gstr1_due)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-[#5a5a53]">GSTR-3B due</span>
          <span className={`font-mono font-bold ${dueSoon ? "text-[#b83a26]" : "text-[#0a0a0a]"}`}>
            {dateFmt(gst.gstr3b_due)} {dueSoon && `· in ${daysToDue}d`}
          </span>
        </div>
      </div>
      {gst.gstin && (
        <p className="text-[10px] text-[#a3a39b] mt-3 font-mono uppercase tracking-wider">{gst.gstin}</p>
      )}
    </div>
  );
}

function GstExceededCard({ gst, fy }) {
  return (
    <div className="tc-card p-6 border border-[#b83a26] bg-[#fff4f1]" data-testid="compliance-gst-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="tc-overline" style={{ color: "#b83a26" }}>GST registration overdue · {fy}</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-white bg-[#b83a26] px-2 py-0.5 rounded-sm">
          <AlertCircle size={11} /> Action needed
        </span>
      </div>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap">
        <span className="font-['Outfit'] text-2xl md:text-3xl font-bold tracking-tight font-mono break-words text-[#b83a26]">{inr(gst.turnover_ytd)}</span>
        <span className="text-xs text-[#5a5a53]">crossed ₹{(gst.threshold / 100000).toFixed(0)}L threshold</span>
      </div>
      <p className="text-sm text-[#0a0a0a] mt-4 leading-relaxed font-medium">{gst.message}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <a
          href="https://reg.gst.gov.in/registration/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#b83a26] hover:bg-[#9a2f1f] text-white text-xs font-bold uppercase tracking-wider rounded-md transition-colors"
          data-testid="gst-register-cta"
        >
          Register on GST portal <ArrowUpRight size={12} />
        </a>
        <Link to="/app/settings" className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-[#5a5a53] hover:text-[#0a0a0a]">
          Already registered? Add GSTIN
        </Link>
      </div>
    </div>
  );
}

function GstThresholdCard({ gst, fy }) {
  const pct = Math.min(100, gst.percent || 0);
  const barColor = gst.status === "approaching" ? "#d4b872" : "#d95d39";
  return (
    <div className="tc-card p-6" data-testid="compliance-gst-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="tc-overline">GST threshold · {fy}</p>
        <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-sm ${
          gst.status === "approaching" ? "text-[#8c6d2a] bg-[#fffaf0]" : "text-[#5a5a53] bg-[#f4f1eb]"
        }`}>
          {gst.status}
        </span>
      </div>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap">
        <span className="font-['Outfit'] text-2xl md:text-3xl font-bold tracking-tight font-mono break-words">{inr(gst.turnover_ytd)}</span>
        <span className="text-xs text-[#5a5a53]">of {inr(gst.threshold)}</span>
      </div>
      <div className="mt-3 h-2 bg-[#f4f1eb] rounded-sm overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${Math.max(pct, 2)}%`, background: barColor }} />
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-[#5a5a53]">
        <span>FY turnover</span>
        <span className="font-mono font-bold" style={{ color: barColor }}>{gst.percent}%</span>
      </div>
      <p className="text-xs text-[#5a5a53] mt-4 leading-relaxed">{gst.message}</p>
      {gst.status === "safe" && (
        <p className="text-[11px] text-[#a3a39b] mt-2">
          Threshold: {gst.state_category === "special" ? "₹10L (special category state)" : "₹20L (regular state)"}
        </p>
      )}
    </div>
  );
}

function AdvanceTaxCard({ advance, fy }) {
  const upcoming = advance.quarters.find((q) => q.status === "upcoming" || q.status === "future");
  return (
    <div className="tc-card p-6" data-testid="compliance-advance-tax-card">
      <div className="flex items-center justify-between">
        <p className="tc-overline">Advance tax · {fy}</p>
        {advance.liable ? (
          upcoming?.status === "upcoming" ? (
            <span className="text-[10px] font-bold tracking-wider uppercase text-[#d95d39] bg-[#fff4ed] px-2 py-0.5 rounded-sm">
              Due in 30d
            </span>
          ) : null
        ) : (
          <span className="text-[10px] font-bold tracking-wider uppercase text-[#4a6b53] bg-[#eef3ee] px-2 py-0.5 rounded-sm">
            Not liable
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap">
        <span className="font-['Outfit'] text-2xl md:text-3xl font-bold tracking-tight font-mono break-words">{inr(advance.estimated_annual_tax)}</span>
        <span className="text-xs text-[#5a5a53]">projected annual tax</span>
      </div>
      <p className="text-[11px] text-[#a3a39b] mt-1 font-mono">
        Based on FY-to-date income {inr(advance.projected_annual_income)} · {advance.regime_recommended} regime
      </p>
      <div className="mt-4 space-y-2" data-testid="advance-tax-quarters">
        {advance.quarters.map((q) => (
          <div
            key={q.label}
            className={`flex items-center justify-between px-3 py-2 rounded-md border text-xs ${
              q.status === "past"
                ? "border-[#e5e5e0] bg-[#fafaf8] text-[#a3a39b]"
                : q.status === "upcoming"
                ? "border-[#d95d39] bg-[#fff4ed]"
                : "border-[#e5e5e0] bg-white"
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar size={12} className={q.status === "past" ? "text-[#a3a39b]" : q.status === "upcoming" ? "text-[#d95d39]" : "text-[#5a5a53]"} />
              <span className={`font-medium ${q.status === "past" ? "line-through" : ""}`}>{q.label}</span>
              <span className="text-[#5a5a53]">· {q.percent}% cumulative</span>
            </div>
            <span className={`font-mono font-bold ${q.status === "upcoming" ? "text-[#d95d39]" : "text-[#0a0a0a]"}`}>
              {inr(q.cumulative_amount)}
            </span>
          </div>
        ))}
      </div>
      {!advance.liable && advance.estimated_annual_tax === 0 && (
        <p className="text-[11px] text-[#5a5a53] mt-3">
          You're not liable for advance tax yet (estimated tax &lt; ₹10,000 — Sec 208).
        </p>
      )}
      <p className="text-[10px] text-[#a3a39b] mt-3 italic">{advance.note}</p>
    </div>
  );
}

function TaxReserveKpi({ reserve, onClick }) {
  const value = reserve ? inr(reserve.total_to_set_aside) : "—";
  const warning = !!reserve?.warning;
  // Palette: amber warning OR brand orange normal
  const accent = warning ? "#b83a26" : "#d95d39";
  const bg = warning ? "bg-[#fff4f1]" : "bg-white";
  const ring = warning ? "ring-1 ring-inset ring-[#f5c6b9]" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="kpi-tax-reserve"
      className={`${bg} ${ring} p-4 md:p-6 relative text-left transition-colors hover:bg-[#fff7f2] min-w-0`}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold leading-tight" style={{ color: accent }}>
          Tax to set aside
        </p>
        <PiggyBank size={14} style={{ color: accent }} className="shrink-0" />
      </div>
      <div className="font-['Outfit'] text-xl md:text-3xl font-bold tracking-tight break-words" style={{ color: accent }}>
        {value}
      </div>
      {reserve && (
        <div className="mt-2 text-[11px] text-[#5a5a53] truncate">
          Income tax {inr(reserve.income_tax_accrued_ytd)} + GST {inr(reserve.gst_payable)}
        </div>
      )}
      {warning && (
        <div className="mt-2 text-[11px] font-bold text-[#b83a26] truncate" data-testid="tax-reserve-warning">
          {reserve.warning_message}
        </div>
      )}
      {!warning && reserve && reserve.total_to_set_aside > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1 bg-[#f4f1eb] rounded-sm overflow-hidden">
            <div className="h-full bg-[#d95d39]" style={{ width: `${reserve.parked_percent || 0}%` }} />
          </div>
          <span className="text-[10px] font-mono text-[#5a5a53]">{reserve.parked_percent || 0}% parked</span>
        </div>
      )}
    </button>
  );
}

function TaxReserveDrawer({ open, onOpenChange, reserve, onParked }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && reserve) setAmount(String(reserve.parked_amount || ""));
  }, [open, reserve]);

  if (!reserve) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/tax/reserve/park", {
        parked_amount: Number(amount || 0),
        note: note || null,
      });
      toast.success("Parked amount updated");
      setNote("");
      onParked && onParked();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save");
    } finally { setSaving(false); }
  };

  const parkedPct = reserve.parked_percent || 0;
  const lastMonths = (reserve.monthly_accrual || []).slice(-12);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[#fafaf8]" data-testid="tax-reserve-drawer">
        <SheetHeader>
          <p className="tc-overline" style={{ color: "#d95d39" }}>{reserve.fy_label}</p>
          <SheetTitle className="font-['Outfit'] text-3xl font-bold tracking-tight">
            Tax to set aside
          </SheetTitle>
          <SheetDescription className="text-[#5a5a53]">
            Cash to park right now so advance-tax and GST instalments don't scramble your runway.
          </SheetDescription>
        </SheetHeader>

        {/* Big number */}
        <div className="mt-6 tc-card p-6" data-testid="tax-reserve-breakdown">
          <div className="font-['Outfit'] text-3xl md:text-5xl font-bold tracking-tight text-[#d95d39] break-words">
            {inr(reserve.total_to_set_aside)}
          </div>
          <div className="mt-2 text-sm text-[#5a5a53]">
            Income tax <span className="font-mono font-bold text-[#0a0a0a]">{inr(reserve.income_tax_accrued_ytd)}</span>
            {" + "}
            GST <span className="font-mono font-bold text-[#0a0a0a]">{inr(reserve.gst_payable)}</span>
          </div>
        </div>

        {/* Warning banner */}
        {reserve.warning && (
          <div className="mt-4 rounded-md border border-[#f5c6b9] bg-[#fff4f1] p-4 flex items-start gap-3">
            <AlertCircle size={16} className="text-[#b83a26] mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-bold text-[#b83a26]">Heads up — advance tax instalment near</p>
              <p className="text-[#5a5a53] mt-1">{reserve.warning_message}</p>
              <p className="text-[11px] text-[#a3a39b] mt-1">Missing a milestone triggers 234C interest — roughly 1% per month on the shortfall.</p>
            </div>
          </div>
        )}

        {/* Parked progress */}
        <div className="mt-6 tc-card p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="tc-overline">Parked so far</p>
            <span className="text-xs font-mono text-[#5a5a53]">{parkedPct}%</span>
          </div>
          <Progress value={parkedPct} className="h-2" />
          <p className="mt-2 text-sm text-[#0a0a0a]">
            <span className="font-mono font-bold">{inr(reserve.parked_amount)}</span> of {inr(reserve.total_to_set_aside)}
          </p>

          <div className="mt-4 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Total parked</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0] font-mono"
                placeholder="0"
                data-testid="tax-reserve-parked-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-2 h-11 rounded-md border-[#e5e5e0]"
                placeholder="e.g. Moved to HDFC FD"
                data-testid="tax-reserve-note-input"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="tax-reserve-mark-parked-btn"
              className="w-full bg-[#0a0a0a] hover:bg-[#1a1a1a] text-white rounded-md"
            >
              {saving ? "Saving..." : "Mark as parked"}
            </Button>
          </div>
        </div>

        {/* Projection */}
        <div className="mt-4 tc-card p-5 text-xs text-[#5a5a53] space-y-1">
          <p className="tc-overline text-[#0a0a0a]">YTD snapshot</p>
          <div className="flex justify-between"><span>YTD taxable income</span><span className="font-mono text-[#0a0a0a]">{inr(reserve.projection.ytd_taxable_income)}</span></div>
          <div className="flex justify-between"><span>Income tax on YTD</span><span className="font-mono text-[#0a0a0a]">{inr(reserve.projection.projected_annual_tax)}</span></div>
          <div className="flex justify-between"><span>Regime</span><span className="font-mono uppercase text-[#0a0a0a]">{reserve.projection.regime_recommended}</span></div>
          {reserve.next_due && (
            <div className="flex justify-between pt-2 border-t border-[#e5e5e0] mt-2">
              <span>Next advance-tax milestone</span>
              <span className="font-mono text-[#d95d39] font-bold">{reserve.next_due.label} · {reserve.next_due.percent}%</span>
            </div>
          )}
          <p className="text-[10px] text-[#a3a39b] italic pt-2">{reserve.note}</p>
        </div>

        {/* Monthly accrual */}
        {lastMonths.length > 0 && (
          <div className="mt-4 tc-card p-5" data-testid="tax-reserve-monthly">
            <p className="tc-overline">Month-by-month accrual</p>
            <div className="mt-3 space-y-1.5 text-xs">
              {lastMonths.map((m) => (
                <div key={m.month} className="flex items-center justify-between py-1 border-b border-[#f4f1eb] last:border-b-0">
                  <span className="text-[#5a5a53] font-mono">{m.month}</span>
                  <span className="text-[#a3a39b]">income {inr(m.income_in_month)}</span>
                  <span className="font-mono font-bold text-[#0a0a0a]">{inr(m.accrued_tax)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {(reserve.history || []).length > 0 && (
          <div className="mt-4 tc-card p-5">
            <p className="tc-overline">Park history</p>
            <div className="mt-3 space-y-2 text-xs">
              {reserve.history.slice().reverse().map((h, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-[#f4f1eb] last:border-b-0 pb-1.5">
                  <span className="text-[#5a5a53]">{dateFmt(h.at)}</span>
                  <span className="text-[#5a5a53] italic truncate px-2 flex-1 text-center">{h.note || ""}</span>
                  <span className="font-mono font-bold text-[#0a0a0a]">{inr(h.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}


const CATEGORY_COLORS_DASH = {
  "Software & SaaS": "#d95d39",
  "Travel": "#3d5a8a",
  "Meals": "#d4b872",
  "Office Supplies": "#5a8a6b",
  "Internet & Phone": "#8c6d2a",
  "Equipment": "#6b4aa0",
  "Marketing": "#b83a26",
  "Education": "#4a6b53",
  "Subcontractors": "#0a0a0a",
  "Bank Fees": "#a3a39b",
  "Rent": "#2a446e",
  "Other": "#c5c5b8",
};

function SpendMiniCard({ analytics }) {
  if (!analytics) return null;
  const { this_month_total, month_delta_pct, by_category, fy_total } = analytics;
  if (!fy_total || fy_total === 0) {
    return (
      <div className="tc-card p-6" data-testid="dashboard-spend-empty">
        <p className="tc-overline">Where money goes</p>
        <p className="mt-3 text-sm text-[#5a5a53] leading-relaxed">
          Import a bank statement and see your spending heatmap, top vendors and 6-month trend in one click.
        </p>
        <Link to="/app/import" className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-[#d95d39] hover:underline" data-testid="dashboard-spend-import-link">
          Import statement <ArrowUpRight size={12} />
        </Link>
      </div>
    );
  }
  const top3 = by_category.slice(0, 3);
  const top3Total = top3.reduce((s, c) => s + c.amount, 0);
  const trendUp = month_delta_pct != null && month_delta_pct > 0;
  return (
    <div className="tc-card p-6" data-testid="dashboard-spend-card">
      <div className="flex items-center justify-between">
        <p className="tc-overline">Where money goes</p>
        <Link to="/app/expenses" className="text-[10px] uppercase tracking-wider font-bold text-[#d95d39] hover:underline" data-testid="dashboard-spend-link">
          See all →
        </Link>
      </div>
      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
        <span className="font-['Outfit'] text-2xl font-bold tracking-tight font-mono">{inr(this_month_total)}</span>
        <span className="text-xs text-[#5a5a53]">this month</span>
        {month_delta_pct != null && (
          <span className={`text-[10px] font-bold ml-auto ${trendUp ? "text-[#b83a26]" : "text-[#4a6b53]"}`}>
            {trendUp ? "↑" : "↓"} {Math.abs(month_delta_pct)}%
          </span>
        )}
      </div>
      <div className="mt-4 space-y-2">
        {top3.map((c) => {
          const pct = top3Total > 0 ? (c.amount / top3Total) * 100 : 0;
          const color = CATEGORY_COLORS_DASH[c.category] || "#a3a39b";
          return (
            <div key={c.category}>
              <div className="flex items-center justify-between text-xs mb-1 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="block w-2 h-2 rounded-sm shrink-0" style={{ background: color }} />
                  <span className="truncate text-[#0a0a0a]">{c.category}</span>
                </div>
                <span className="font-mono text-[#5a5a53] text-[11px] shrink-0">{inr(c.amount)}</span>
              </div>
              <div className="h-1 bg-[#f4f1eb] rounded-sm overflow-hidden">
                <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

